import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Footer } from '../components/SharedLayout'

const colors = {
  'primary-teal': '#145F6B',
  'warm-copper': '#9B775D',
  'dark-teal': '#083B3A',
  'cream': '#F6F6F1',
  'near-black': '#0F0F0F',
}

export default function PitchDeckPage() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  }

  const useOfFunds = [
    { category: 'Working Capital', amount: '$240,000', percent: '20%', description: 'Salaries, onboarding, insurance, lease/utilities, day-to-day operations during launch phase' },
    { category: 'Marketing & Customer Acquisition', amount: '$100,000', percent: '8%', description: 'Digital campaigns, referral incentives, PR, community partnerships, content creation' },
    { category: 'Equipment & Tools', amount: '$60,000', percent: '5%', description: 'Inspection equipment, safety tools, vehicles, uniforms for Hudson Specialists' },
    { category: 'Technology & App Development', amount: '$100,000', percent: '8%', description: 'Proprietary app, CRM integrations, reporting dashboards, AI-driven tools' },
    { category: 'Recruiting & Staffing', amount: '$300,000', percent: '25%', description: 'Sales team, Maintenance Coordinators, and Maintenance Technicians recruitment' },
    { category: 'Handyman Acquisitions', amount: '$250,000', percent: '21%', description: 'Acquiring existing handyman operations to accelerate growth and create immediate cash flow' },
    { category: 'Fleet & Branding', amount: '$100,000', percent: '8%', description: '3+ wrapped trucks for Sales, Inspections, and Maintenance with branded marketing' },
    { category: 'Reserve', amount: '$50,000', percent: '4%', description: 'Working capital reserve for operational flexibility' },
  ]

  const products = [
    { 
      name: 'Blueprint (App Only)', 
      price: '$300/yr', 
      mix: '50%',
      description: 'MyHome Blueprint™ maintenance planning, document vault, property management app',
    },
    { 
      name: 'Blueprint + Inspection', 
      price: '$600/yr', 
      mix: '50%',
      description: 'Full Blueprint access plus annual comprehensive home inspection',
    },
    { 
      name: 'Membership: <1,400 SF', 
      price: '$399/mo', 
      mix: '30%',
      description: 'White-glove service for smaller homes with dedicated Hudson professional',
    },
    { 
      name: 'Membership: 1,401-3,000 SF', 
      price: '$449/mo', 
      mix: '50%',
      description: 'Full-service membership for mid-size homes — our core offering',
    },
    { 
      name: 'Membership: 3,001+ SF', 
      price: '$549/mo', 
      mix: '20%',
      description: 'Premium tier for larger luxury homes with enhanced service scope',
    },
    { 
      name: 'Concierge Services', 
      price: '$200/request', 
      mix: 'Add-on',
      description: 'Coordination of wish-list projects: remodeling, upgrades, contractor oversight',
    },
  ]

  const projections = [
    { year: '2025', revenue: '$42.5K', homes: '—', expenses: '$81K', pretax: '-$39K', netCash: '-$59K', note: 'Launch phase' },
    { year: '2026', revenue: '$942K', homes: '~85', expenses: '$560K', pretax: '$382K', netCash: '$223K', note: 'Scale year' },
    { year: '2027', revenue: '$2.16M', homes: '~200', expenses: '$1.23M', pretax: '$930K', netCash: '$666K', note: 'Regional expansion' },
    { year: '2028', revenue: '$3.43M', homes: '~340', expenses: '$2.02M', pretax: '$1.41M', netCash: '$1.04M', note: 'Market leadership' },
  ]

  const team = [
    {
      name: 'Joseph Schafbuch',
      role: 'Founder & CEO',
      description: 'Experienced multi-business entrepreneur in real estate and property services. Strategic Coach Free Zone member. Provides entrepreneurial vision, growth strategy, partnerships, and financing.',
      equity: '100% Owner',
    },
    {
      name: 'Cassi Luree, SHRM-CP',
      role: 'Director of Operations',
      description: 'Certified HR professional with expertise in people operations, compliance, and scaling organizational systems. Strategic Coach member. Responsible for day-to-day operations, staff development, and building Hudson\'s people-first culture.',
      equity: 'Key Leadership',
    },
    {
      name: 'Jimmy Becker',
      role: 'Fractional CFO',
      description: 'Advisory role providing financial oversight, projections, and strategic financial planning for SBA loan process and ongoing growth.',
      equity: 'Advisor',
    },
    {
      name: 'Bonnie Le Page',
      role: 'Fractional CMO',
      description: 'Advisory role overseeing marketing strategy, brand positioning, and go-to-market execution for Des Moines Metro and planned national expansion.',
      equity: 'Advisor',
    },
  ]

  const milestones = [
    { phase: 'Completed', items: ['Market research & brand guidelines', 'Proprietary tools piloted (MyHome Snapshot™, Score™, Blueprint™)', 'Licensed technical leadership hired', 'App development in testing', '$350,000 owner equity invested'] },
    { phase: 'Year 1 Goals', items: ['240 homes enrolled in Des Moines Metro', '~$1.4M annualized revenue', '10+ Hudson Specialists hired', 'App launched for scheduling & reporting', 'Referral partnerships established'] },
    { phase: 'Year 3 Goals', items: ['760+ homes across Iowa', '~$4.4M annualized revenue', '20+ Hudson Specialists', 'Recognized as Iowa\'s premier brand', 'DSCR exceeds 1.5x'] },
    { phase: '10-Year Vision', items: ['60,000 homes under management', '100+ markets nationally', '1,000+ Hudson Specialists', 'Midwest leading home concierge', 'National franchise model'] },
  ]

  const competitiveAdvantages = [
    { title: 'Proprietary Technology', description: 'MyHome Snapshot™, MyHome Score™, and MyHome Blueprint™ provide data-driven insights no competitor offers' },
    { title: 'Dedicated Specialist Model', description: 'One Hudson assigned to each home builds trust, familiarity, and consistency unlike one-off contractors' },
    { title: 'Whole-Home Care', description: 'Comprehensive approach vs. system-by-system care from traditional providers' },
    { title: 'Preventive vs. Reactive', description: 'Subscription model prevents costly breakdowns; extends system lifespan by 10-15%' },
    { title: 'White Glove, Blue Collar', description: 'High-touch service with hands-on expertise — premium experience at every interaction' },
    { title: 'Recurring Revenue Model', description: 'Predictable monthly subscriptions provide stable cash flow and high retention' },
  ]

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-12 py-4" style={{ backgroundColor: colors['near-black'] }}>
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Secondary_Logo_White.png" 
              alt="Hudson My Home" 
              className="h-8"
            />
          </Link>
          <div className="flex items-center gap-6">
            <span className="text-[11px] tracking-[0.2em] uppercase" style={{ color: colors['warm-copper'] }}>
              Confidential Investment Overview
            </span>
          </div>
        </div>
      </nav>

      {/* Cover Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12" style={{ background: `linear-gradient(180deg, ${colors['near-black']} 0%, ${colors['dark-teal']} 100%)` }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn} className="text-center">
            <p className="text-[11px] tracking-[0.3em] uppercase mb-6" style={{ color: colors['warm-copper'] }}>
              SBA 7(a) Loan Application
            </p>
            <h1 className="font-serif text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-[-0.02em] text-white mb-6">
              Hudson My Home
            </h1>
            <p className="text-xl lg:text-2xl text-grey-300 max-w-3xl mx-auto mb-8 leading-relaxed">
              Subscription-based preventative home maintenance and concierge services
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-sm">
              <div className="text-center">
                <p className="text-grey-400 text-xs uppercase tracking-wider mb-1">Loan Request</p>
                <p className="text-white text-2xl font-medium">$1,200,000</p>
              </div>
              <div className="text-center">
                <p className="text-grey-400 text-xs uppercase tracking-wider mb-1">Owner Equity</p>
                <p className="text-white text-2xl font-medium">$350,000</p>
              </div>
              <div className="text-center">
                <p className="text-grey-400 text-xs uppercase tracking-wider mb-1">Year 3 Revenue</p>
                <p className="text-white text-2xl font-medium">$4.4M</p>
              </div>
              <div className="text-center">
                <p className="text-grey-400 text-xs uppercase tracking-wider mb-1">DSCR (Year 2+)</p>
                <p className="text-white text-2xl font-medium">&gt;1.5x</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="py-20 px-6 lg:px-12" style={{ backgroundColor: 'white' }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              01 — Executive Summary
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-grey-900 mb-8">
              The Opportunity
            </h2>
          </motion.div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div {...fadeIn} className="space-y-6">
              <p className="text-lg text-grey-700 leading-relaxed">
                <strong>Hudson My Home LC</strong> is a subscription-based preventative home maintenance and concierge services company headquartered in Des Moines, Iowa. We provide proactive, scheduled home care designed to reduce emergency repairs, extend the life of major home systems, and deliver predictable maintenance costs for homeowners.
              </p>
              <p className="text-lg text-grey-700 leading-relaxed">
                Founded in 2023, Hudson addresses a common homeowner challenge: limited time and expertise to manage routine maintenance, which leads to reactive spending and avoidable system failures. We replace episodic, emergency-driven services with a recurring preventative model delivered through monthly visits, quarterly reporting, and concierge coordination.
              </p>
              <p className="text-lg text-grey-700 leading-relaxed">
                The leadership team has invested over <strong>$350,000 of owner equity</strong> into research, infrastructure, staffing, branding, and proprietary technology. The company has completed market validation, built core operating systems, hired licensed technical leadership, and is in its launch and early scaling phase.
              </p>
            </motion.div>
            
            <motion.div {...fadeIn} transition={{ delay: 0.2 }}>
              <div className="p-8 rounded-sm" style={{ backgroundColor: colors.cream }}>
                <h3 className="font-medium text-grey-900 text-lg mb-6">Company Overview</h3>
                <div className="space-y-4">
                  <div className="flex justify-between pb-3 border-b border-grey-200">
                    <span className="text-grey-600">Company</span>
                    <span className="font-medium text-grey-900">Hudson My Home LC</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b border-grey-200">
                    <span className="text-grey-600">Founded</span>
                    <span className="font-medium text-grey-900">2023</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b border-grey-200">
                    <span className="text-grey-600">Structure</span>
                    <span className="font-medium text-grey-900">Iowa LLC</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b border-grey-200">
                    <span className="text-grey-600">Headquarters</span>
                    <span className="font-medium text-grey-900">Des Moines, Iowa</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b border-grey-200">
                    <span className="text-grey-600">Stage</span>
                    <span className="font-medium text-grey-900">Launch Phase</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b border-grey-200">
                    <span className="text-grey-600">Owner Equity Invested</span>
                    <span className="font-medium text-grey-900">$350,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-grey-600">Ownership</span>
                    <span className="font-medium text-grey-900">100% Joseph Schafbuch</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-20 px-6 lg:px-12" style={{ backgroundColor: colors.cream }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              02 — Problem & Solution
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-grey-900 mb-12">
              Why Hudson Exists
            </h2>
          </motion.div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div {...fadeIn} className="bg-white p-8 rounded-sm">
              <h3 className="font-medium text-grey-900 text-xl mb-6 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm text-white" style={{ backgroundColor: colors['primary-teal'] }}>!</span>
                The Problem
              </h3>
              <ul className="space-y-4 text-grey-700">
                <li className="flex gap-3">
                  <span style={{ color: colors['warm-copper'] }}>•</span>
                  <span>Most homeowners lack the time, knowledge, or discipline to manage routine maintenance</span>
                </li>
                <li className="flex gap-3">
                  <span style={{ color: colors['warm-copper'] }}>•</span>
                  <span>This leads to costly emergency repairs, system breakdowns, and diminished asset value</span>
                </li>
                <li className="flex gap-3">
                  <span style={{ color: colors['warm-copper'] }}>•</span>
                  <span>71% of homeowners delayed at least one project due to economic pressures (Angi 2025)</span>
                </li>
                <li className="flex gap-3">
                  <span style={{ color: colors['warm-copper'] }}>•</span>
                  <span>Homeowners spend 5-10% of home value annually on reactive maintenance</span>
                </li>
                <li className="flex gap-3">
                  <span style={{ color: colors['warm-copper'] }}>•</span>
                  <span>No trusted, comprehensive home management partner exists in most markets</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="bg-white p-8 rounded-sm">
              <h3 className="font-medium text-grey-900 text-xl mb-6 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm text-white" style={{ backgroundColor: colors['warm-copper'] }}>✓</span>
                The Solution
              </h3>
              <ul className="space-y-4 text-grey-700">
                <li className="flex gap-3">
                  <span style={{ color: colors['primary-teal'] }}>•</span>
                  <span>Subscription-based preventative maintenance with predictable monthly costs ($399-$549)</span>
                </li>
                <li className="flex gap-3">
                  <span style={{ color: colors['primary-teal'] }}>•</span>
                  <span>Dedicated Hudson Specialist assigned to each home — builds trust through familiarity</span>
                </li>
                <li className="flex gap-3">
                  <span style={{ color: colors['primary-teal'] }}>•</span>
                  <span>Monthly proactive visits catch issues before they become expensive problems</span>
                </li>
                <li className="flex gap-3">
                  <span style={{ color: colors['primary-teal'] }}>•</span>
                  <span>Proprietary MyHome Snapshot™, Score™, and Blueprint™ tools provide data-driven insights</span>
                </li>
                <li className="flex gap-3">
                  <span style={{ color: colors['primary-teal'] }}>•</span>
                  <span>Preventive services extend system lifespan and reduce repair spending by 10-15% annually</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Business Model & Products */}
      <section className="py-20 px-6 lg:px-12" style={{ backgroundColor: 'white' }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              03 — Business Model
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-grey-900 mb-4">
              Product Portfolio & Revenue Mix
            </h2>
            <p className="text-lg text-grey-600 mb-12 max-w-2xl">
              Revenue is generated primarily through recurring membership fees, with concierge upsells, a la carte services, and business partnerships creating additional streams.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={product.name}
                {...fadeIn}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-sm border border-grey-200 hover:border-grey-300 transition-colors"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-medium text-grey-900">{product.name}</h3>
                  <span className="text-sm px-2 py-1 rounded" style={{ backgroundColor: colors.cream, color: colors['primary-teal'] }}>
                    {product.mix}
                  </span>
                </div>
                <p className="text-2xl font-medium mb-3" style={{ color: colors['primary-teal'] }}>
                  {product.price}
                </p>
                <p className="text-sm text-grey-600">{product.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeIn} className="mt-12 p-8 rounded-sm" style={{ backgroundColor: colors.cream }}>
            <h3 className="font-medium text-grey-900 text-lg mb-4">Premier Estate Membership Includes</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-grey-700">
              <div>
                <p className="font-medium text-grey-900 mb-2">Monthly Proactive Visits</p>
                <p className="text-sm">HVAC filter replacement, plumbing checks, gutter inspection, smoke detector testing, seasonal preparation</p>
              </div>
              <div>
                <p className="font-medium text-grey-900 mb-2">Initial Home Assessment</p>
                <p className="text-sm">Detailed health inspection via MyHome Snapshot™, Score™, and Blueprint™ tools</p>
              </div>
              <div>
                <p className="font-medium text-grey-900 mb-2">Quarterly Reports</p>
                <p className="text-sm">Data-driven updates with recommended improvements to protect property value</p>
              </div>
              <div>
                <p className="font-medium text-grey-900 mb-2">Concierge Coordination</p>
                <p className="text-sm">Priority scheduling for vetted contractors and service providers</p>
              </div>
              <div>
                <p className="font-medium text-grey-900 mb-2">24/7 Emergency Support</p>
                <p className="text-sm">Round-the-clock access to support for urgent issues</p>
              </div>
              <div>
                <p className="font-medium text-grey-900 mb-2">MyHome Guarantee</p>
                <p className="text-sm">Unmatched care, or we make it right</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Already in Place */}
      <section className="py-20 px-6 lg:px-12" style={{ background: `linear-gradient(180deg, ${colors['dark-teal']} 0%, ${colors['near-black']} 100%)` }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              04 — Current Status
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-white mb-12">
              What's Already in Place
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.phase}
                {...fadeIn}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-sm"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
              >
                <h3 className="font-medium text-white mb-4 pb-3 border-b border-white/10">
                  {milestone.phase}
                </h3>
                <ul className="space-y-3">
                  {milestone.items.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-grey-300">
                      <span style={{ color: colors['warm-copper'] }}>✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-20 px-6 lg:px-12" style={{ backgroundColor: 'white' }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              05 — Market Opportunity
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-grey-900 mb-12">
              Massive & Growing Market
            </h2>
          </motion.div>
          
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <motion.div {...fadeIn} className="text-center p-8 rounded-sm" style={{ backgroundColor: colors.cream }}>
              <p className="text-[clamp(2.5rem,5vw,4rem)] font-serif mb-2" style={{ color: colors['primary-teal'] }}>$567B</p>
              <p className="text-grey-600">spent on home renovations & repairs in 2022</p>
            </motion.div>
            <motion.div {...fadeIn} transition={{ delay: 0.1 }} className="text-center p-8 rounded-sm" style={{ backgroundColor: colors.cream }}>
              <p className="text-[clamp(2.5rem,5vw,4rem)] font-serif mb-2" style={{ color: colors['primary-teal'] }}>$1T+</p>
              <p className="text-grey-600">projected home services market by 2029</p>
            </motion.div>
            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="text-center p-8 rounded-sm" style={{ backgroundColor: colors.cream }}>
              <p className="text-[clamp(2.5rem,5vw,4rem)] font-serif mb-2" style={{ color: colors['primary-teal'] }}>40%+</p>
              <p className="text-grey-600">CAGR in preventive/on-demand segments</p>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div {...fadeIn}>
              <h3 className="font-medium text-grey-900 text-xl mb-6">Target Market: Des Moines Metro</h3>
              <div className="space-y-4 text-grey-700">
                <p><strong>750,000</strong> residents in the greater metro area</p>
                <p><strong>~12,950</strong> housing units in the city, 56% single-family detached</p>
                <p><strong>59%</strong> owner-occupied, 88% in single-family homes</p>
                <p><strong>7,000-8,000</strong> owner-occupied detached homes (target segment)</p>
                <p><strong>5% penetration</strong> = 350-400 premium membership subscribers</p>
              </div>
            </motion.div>
            
            <motion.div {...fadeIn} transition={{ delay: 0.2 }}>
              <h3 className="font-medium text-grey-900 text-xl mb-6">Ideal Customer Profile</h3>
              <div className="space-y-4 text-grey-700">
                <p><strong>Demographics:</strong> Ages 30-50, often married with children</p>
                <p><strong>Income:</strong> $100,000+ household income</p>
                <p><strong>Psychographics:</strong> Dual-income professionals who value time freedom</p>
                <p><strong>Pain Point:</strong> Limited time to manage home maintenance</p>
                <p><strong>Desire:</strong> Peace of mind, proactive care, trusted partner</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-20 px-6 lg:px-12" style={{ backgroundColor: colors.cream }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              06 — Competitive Advantages
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-grey-900 mb-12">
              Why Hudson Wins
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competitiveAdvantages.map((advantage, index) => (
              <motion.div
                key={advantage.title}
                {...fadeIn}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-sm"
              >
                <h3 className="font-medium text-grey-900 mb-3">{advantage.title}</h3>
                <p className="text-grey-600">{advantage.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeIn} className="mt-12 p-8 bg-white rounded-sm">
            <h3 className="font-medium text-grey-900 text-lg mb-6">Competitive Landscape</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <p className="font-medium text-grey-900 mb-2">Home Warranty Providers</p>
                <p className="text-sm text-grey-600 mb-2">American Home Shield, HomeServe</p>
                <p className="text-sm" style={{ color: colors['primary-teal'] }}>→ Focus on reactive repairs, not prevention</p>
              </div>
              <div>
                <p className="font-medium text-grey-900 mb-2">On-Demand Platforms</p>
                <p className="text-sm text-grey-600 mb-2">Thumbtack, TaskRabbit, Angi</p>
                <p className="text-sm" style={{ color: colors['primary-teal'] }}>→ One-off services, no relationship, homeowner still manages</p>
              </div>
              <div>
                <p className="font-medium text-grey-900 mb-2">Local Subscription Services</p>
                <p className="text-sm text-grey-600 mb-2">None found in Des Moines market</p>
                <p className="text-sm" style={{ color: colors['primary-teal'] }}>→ Hudson is first-to-market with this model</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Financial Projections */}
      <section className="py-20 px-6 lg:px-12" style={{ backgroundColor: 'white' }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              07 — Financial Projections
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-grey-900 mb-4">
              4-Year Financial Forecast
            </h2>
            <p className="text-lg text-grey-600 mb-12">
              Conservative projections with 10% attrition modeled. DSCR exceeds 1.5x beginning in Year 2.
            </p>
          </motion.div>
          
          <motion.div {...fadeIn} className="overflow-x-auto mb-12">
            <table className="w-full">
              <thead>
                <tr className="border-b border-grey-200">
                  <th className="text-left py-4 pr-6 font-medium text-grey-600 text-sm">Year</th>
                  <th className="text-right py-4 px-6 font-medium text-grey-600 text-sm">Revenue</th>
                  <th className="text-right py-4 px-6 font-medium text-grey-600 text-sm">Homes</th>
                  <th className="text-right py-4 px-6 font-medium text-grey-600 text-sm">Expenses</th>
                  <th className="text-right py-4 px-6 font-medium text-grey-600 text-sm">Pretax Profit</th>
                  <th className="text-right py-4 pl-6 font-medium text-grey-600 text-sm">Net Cash</th>
                </tr>
              </thead>
              <tbody>
                {projections.map((row, index) => (
                  <tr key={row.year} className="border-b border-grey-100">
                    <td className="py-4 pr-6">
                      <span className="font-medium text-grey-900">{row.year}</span>
                      <span className="block text-xs text-grey-500">{row.note}</span>
                    </td>
                    <td className="text-right py-4 px-6 font-medium" style={{ color: colors['primary-teal'] }}>{row.revenue}</td>
                    <td className="text-right py-4 px-6 text-grey-600">{row.homes}</td>
                    <td className="text-right py-4 px-6 text-grey-600">{row.expenses}</td>
                    <td className="text-right py-4 px-6 font-medium text-grey-900">{row.pretax}</td>
                    <td className="text-right py-4 pl-6 font-medium" style={{ color: row.netCash.includes('-') ? '#dc2626' : colors['primary-teal'] }}>{row.netCash}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div {...fadeIn} className="p-8 rounded-sm" style={{ backgroundColor: colors.cream }}>
              <h3 className="font-medium text-grey-900 text-lg mb-4">Revenue Assumptions</h3>
              <ul className="space-y-3 text-grey-700">
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> 20 new homes added per month starting Nov 2025</li>
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> 50% conversion rate on 20 appointments/week</li>
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> 3-year client retention with 10% annual attrition</li>
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> 2% annual revenue growth from existing clients</li>
              </ul>
            </motion.div>
            
            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="p-8 rounded-sm" style={{ backgroundColor: colors.cream }}>
              <h3 className="font-medium text-grey-900 text-lg mb-4">Expense Structure</h3>
              <ul className="space-y-3 text-grey-700">
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> Technicians: $40,000/year base</li>
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> Vehicles: $42,000 each, amortized over 5 years</li>
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> 8% profit allocated for team bonuses</li>
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> 3% expenses scaled 1.5x annually</li>
                <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>•</span> Annual debt service: $81,373</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use of Funds */}
      <section className="py-20 px-6 lg:px-12" style={{ background: `linear-gradient(180deg, ${colors['dark-teal']} 0%, ${colors['near-black']} 100%)` }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              08 — Use of Funds
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-white mb-4">
              $1,200,000 SBA 7(a) Loan
            </h2>
            <p className="text-xl text-grey-300 mb-12">
              Strategic allocation for working capital, staffing, marketing, equipment, technology, and acquisition.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {useOfFunds.map((item, index) => (
              <motion.div
                key={item.category}
                {...fadeIn}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-sm"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-medium text-white">{item.category}</h3>
                  <span className="text-sm px-2 py-1 rounded" style={{ backgroundColor: colors['warm-copper'], color: 'white' }}>
                    {item.percent}
                  </span>
                </div>
                <p className="text-2xl font-medium text-white mb-2">{item.amount}</p>
                <p className="text-sm text-grey-400">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeIn} className="mt-12 p-8 rounded-sm" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <h3 className="font-medium text-white text-lg mb-4">Loan Terms Requested</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <p className="text-grey-400 text-sm mb-1">Loan Amount</p>
                <p className="text-white text-xl font-medium">$1,200,000</p>
              </div>
              <div>
                <p className="text-grey-400 text-sm mb-1">Loan Type</p>
                <p className="text-white text-xl font-medium">SBA 7(a)</p>
              </div>
              <div>
                <p className="text-grey-400 text-sm mb-1">Term</p>
                <p className="text-white text-xl font-medium">10 Years</p>
              </div>
              <div>
                <p className="text-grey-400 text-sm mb-1">Annual Debt Service</p>
                <p className="text-white text-xl font-medium">$81,373</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 px-6 lg:px-12" style={{ backgroundColor: 'white' }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              09 — Leadership Team
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-grey-900 mb-4">
              Experienced Leadership
            </h2>
            <p className="text-lg text-grey-600 mb-12 max-w-2xl">
              Joe and Cassi have a four-year history of working together, successfully building trust through collaboration and delivering results across multiple operational projects.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                {...fadeIn}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-sm"
                style={{ backgroundColor: colors.cream }}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-medium text-grey-900 text-xl">{member.name}</h3>
                    <p className="text-sm" style={{ color: colors['primary-teal'] }}>{member.role}</p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: colors['warm-copper'], color: 'white' }}>
                    {member.equity}
                  </span>
                </div>
                <p className="text-grey-700">{member.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeIn} className="mt-12 p-8 rounded-sm border border-grey-200">
            <h3 className="font-medium text-grey-900 text-lg mb-4">Advisory & Support Network</h3>
            <div className="grid md:grid-cols-2 gap-6 text-grey-700">
              <div>
                <p className="font-medium text-grey-900 mb-2">Strategic Coach Peer Group</p>
                <p className="text-sm">Provides insight and accountability from some of the nation's top entrepreneurs</p>
              </div>
              <div>
                <p className="font-medium text-grey-900 mb-2">Professional Service Providers</p>
                <p className="text-sm">External accountant, attorney, marketing team, and insurance advisor</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Ask */}
      <section className="py-20 px-6 lg:px-12" style={{ background: `linear-gradient(180deg, ${colors['primary-teal']} 0%, ${colors['dark-teal']} 100%)` }}>
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              10 — The Ask
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-white mb-8">
              $1,200,000 SBA 7(a) Loan
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-12">
              Hudson My Home combines experienced ownership, meaningful equity investment, and a scalable subscription model with predictable recurring revenue, positioning the company for sustainable profitability and reliable SBA loan repayment.
            </p>
          </motion.div>
          
          <motion.div {...fadeIn} className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="p-6 rounded-sm" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <p className="text-3xl font-medium text-white mb-2">$1.2M</p>
              <p className="text-sm text-white/70">Loan Request</p>
            </div>
            <div className="p-6 rounded-sm" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <p className="text-3xl font-medium text-white mb-2">$350K</p>
              <p className="text-sm text-white/70">Owner Equity Invested</p>
            </div>
            <div className="p-6 rounded-sm" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <p className="text-3xl font-medium text-white mb-2">&gt;1.5x</p>
              <p className="text-sm text-white/70">DSCR (Year 2+)</p>
            </div>
            <div className="p-6 rounded-sm" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <p className="text-3xl font-medium text-white mb-2">$4.4M</p>
              <p className="text-sm text-white/70">Year 3 Revenue</p>
            </div>
          </motion.div>

          <motion.div {...fadeIn} className="inline-block p-8 rounded-sm bg-white text-left max-w-2xl">
            <h3 className="font-medium text-grey-900 text-lg mb-4">Collateral & Guarantee</h3>
            <ul className="space-y-3 text-grey-700">
              <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>✓</span> Business assets (equipment, technology, vehicles)</li>
              <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>✓</span> Personal guarantee from Joseph Schafbuch (100% owner)</li>
              <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>✓</span> $350,000 owner equity already invested</li>
              <li className="flex gap-2"><span style={{ color: colors['warm-copper'] }}>✓</span> Recurring revenue model with high retention</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Exit Strategy */}
      <section className="py-20 px-6 lg:px-12" style={{ backgroundColor: colors.cream }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeIn}>
            <p className="text-[11px] tracking-[0.3em] uppercase mb-4" style={{ color: colors['warm-copper'] }}>
              11 — Exit Strategy
            </p>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-grey-900 mb-12">
              Long-Term Protection
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div {...fadeIn} className="bg-white p-8 rounded-sm">
              <h3 className="font-medium text-grey-900 text-lg mb-4">Succession Planning</h3>
              <p className="text-grey-700">
                Internal development tracks for future managers and potential executives. Operational control can transition to Director of Operations or senior leader, ensuring continuity.
              </p>
            </motion.div>
            <motion.div {...fadeIn} transition={{ delay: 0.1 }} className="bg-white p-8 rounded-sm">
              <h3 className="font-medium text-grey-900 text-lg mb-4">Sale of Business</h3>
              <p className="text-grey-700">
                Likely acquirers include regional/national home services companies, property management groups, or private equity firms specializing in subscription-based businesses.
              </p>
            </motion.div>
            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="bg-white p-8 rounded-sm">
              <h3 className="font-medium text-grey-900 text-lg mb-4">Owner Transition</h3>
              <p className="text-grey-700">
                In the event of retirement, disability, or other major life event, Hudson will execute a transition plan to new ownership with support from advisors.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
